import { getOptimizedOrder, getDistanceMatrix, getRouteLegTimes } from './googleMapsTravel.js';

const HOTEL_TRAVEL_HRS = 0.42; // 25 min hotel drive estimate

// ─── Optimize entire route (redistribute across days) ─────

/**
 * @param {Array} jobs - All JOB-type route_items (flattened with crm_job fields)
 * @param {{ address: string }} homeBase
 * @param {'farthest_first' | 'sequential'} strategy
 * @param {Object} dayLimits - { dayNumber: maxHours } for extended days
 * @param {number} defaultDayHours - default 8
 * @param {Function} onProgress - (current, total, description)
 * @returns {Array} [{ id, day_number, sort_order }]
 */
export const optimizeRoute = async (jobs, homeBase, strategy, dayLimits = {}, defaultDayHours = 8, onProgress) => {
  const pinned = jobs.filter(j => j.is_pinned);
  const unpinned = jobs.filter(j => !j.is_pinned);

  if (unpinned.length === 0) return [];

  const withLocation = unpinned.filter(j => j.location);
  const noLocation = unpinned.filter(j => !j.location);
  const getLimit = (dn) => dayLimits[dn] || defaultDayHours;

  // ── Step 1: Sort by road distance from home ──
  onProgress?.(0, 5, 'Calculating road distances from home base...');
  let sorted = withLocation;

  if (withLocation.length > 0) {
    try {
      const destinations = withLocation.map(j => j.location);
      const distances = await getDistanceMatrix(homeBase.address, destinations);
      const tagged = withLocation.map((job, i) => ({
        ...job,
        _distFromHome: distances[i].distanceKm ?? 9999,
        _travelFromHome: distances[i].durationHours ?? 0.5,
      }));
      tagged.sort((a, b) => strategy === 'farthest_first'
        ? b._distFromHome - a._distFromHome
        : a._distFromHome - b._distFromHome
      );
      sorted = tagged;
    } catch (err) {
      console.warn('Distance Matrix failed, using original order:', err.message);
    }
  }

  // ── Step 2: Initial day split (work hours + travel buffer) ──
  onProgress?.(1, 5, 'Splitting jobs into days...');

  const pinnedHoursPerDay = {};
  for (const p of pinned) {
    const d = p.day_number;
    pinnedHoursPerDay[d] = (pinnedHoursPerDay[d] || 0) + (parseFloat(p.estimated_hours) || 0);
  }

  // dayGroups: { dayNum: [job, job, ...] }
  let dayGroups = {};
  let dayNum = 1;
  let dayHours = pinnedHoursPerDay[dayNum] || 0;
  let dayJobCount = 0;

  for (const job of sorted) {
    const workHrs = parseFloat(job.estimated_hours) || 1;

    // Travel estimate: first job on day gets drive from home (Day 1) or hotel buffer
    const travelIn = dayJobCount === 0
      ? (dayNum === 1 ? (job._travelFromHome || 0.5) : HOTEL_TRAVEL_HRS)
      : HOTEL_TRAVEL_HRS; // rough inter-job estimate
    const endBuffer = HOTEL_TRAVEL_HRS; // reserve for end-of-day drive

    const projected = dayHours + workHrs + travelIn + endBuffer;
    const limit = getLimit(dayNum);

    if (projected > limit && dayJobCount > 0) {
      dayNum++;
      dayHours = pinnedHoursPerDay[dayNum] || 0;
      dayJobCount = 0;
    }

    if (!dayGroups[dayNum]) dayGroups[dayNum] = [];
    dayGroups[dayNum].push(job);

    const actualTravelIn = dayJobCount === 0
      ? (dayNum === 1 ? (job._travelFromHome || 0.5) : HOTEL_TRAVEL_HRS)
      : HOTEL_TRAVEL_HRS;
    dayHours += workHrs + actualTravelIn;
    dayJobCount++;
  }

  // Append no-location jobs to the last day
  if (noLocation.length > 0) {
    if (!dayGroups[dayNum]) dayGroups[dayNum] = [];
    dayGroups[dayNum].push(...noLocation);
  }

  // ── Step 3: Per-day optimization (reorder within each day) ──
  onProgress?.(2, 5, 'Optimizing job order per day...');

  let sortedDays = Object.keys(dayGroups).map(Number).sort((a, b) => a - b);

  const optimizeDayGroup = async (dn, dayJobs) => {
    const jobsWithLoc = dayJobs.filter(j => j.location);
    if (jobsWithLoc.length < 2) return dayJobs;

    const lastDay = sortedDays[sortedDays.length - 1];
    const isFirstDay = dn === sortedDays[0];
    const isLastDay = dn === lastDay;
    const origin = isFirstDay ? homeBase.address : jobsWithLoc[0].location;
    const destination = isLastDay ? homeBase.address : jobsWithLoc[jobsWithLoc.length - 1].location;

    try {
      const order = await getOptimizedOrder(origin, destination, jobsWithLoc.map(j => j.location));
      const reordered = order.map(i => jobsWithLoc[i]);
      const noLocJobs = dayJobs.filter(j => !j.location);
      return [...reordered, ...noLocJobs];
    } catch (err) {
      console.warn(`Day ${dn} optimization failed:`, err.message);
      return dayJobs;
    }
  };

  for (const dn of sortedDays) {
    dayGroups[dn] = await optimizeDayGroup(dn, dayGroups[dn]);
  }

  // ── Step 4: Validate with real travel and rebalance ──
  onProgress?.(3, 5, 'Validating day totals with real travel times...');

  for (let pass = 0; pass < 3; pass++) {
    let anyOverflow = false;
    sortedDays = Object.keys(dayGroups).map(Number).sort((a, b) => a - b);
    const lastDay = sortedDays[sortedDays.length - 1];

    for (let di = 0; di < sortedDays.length; di++) {
      const dn = sortedDays[di];
      const dayJobs = dayGroups[dn];
      const jobsWithLoc = dayJobs.filter(j => j.location);
      if (jobsWithLoc.length === 0) continue;

      // Get real leg times for this day
      const isFirstDay = dn === sortedDays[0];
      const isLastDay = dn === lastDay;
      const startPt = isFirstDay ? homeBase.address : jobsWithLoc[0].location;
      const endPt = isLastDay ? homeBase.address : 'hotel';

      let actualTotal = dayJobs.reduce((s, j) => s + (parseFloat(j.estimated_hours) || 0), 0);
      actualTotal += (pinnedHoursPerDay[dn] || 0);

      // Calculate real travel
      try {
        // For non-last days ending at hotel, use last job as destination (hotel is nearby)
        const dest = isLastDay ? homeBase.address : jobsWithLoc[jobsWithLoc.length - 1].location;
        const legs = await getRouteLegTimes(startPt, dest, jobsWithLoc.map(j => j.location));
        const travelTotal = legs.reduce((s, l) => s + (l.durationHours || 0), 0);
        // Add hotel buffer for non-last days (hotel drive not in legs since dest = last job)
        actualTotal += travelTotal + (isLastDay ? 0 : HOTEL_TRAVEL_HRS);

        onProgress?.(3, 5, `Day ${dn}: ${actualTotal.toFixed(1)}h (limit ${getLimit(dn)}h)`);
      } catch {
        // Can't validate — skip
        continue;
      }

      const limit = getLimit(dn);
      if (actualTotal > limit && dayJobs.length > 1) {
        // Overflow: move last job to next day
        const overflow = dayJobs.pop();
        const nextDn = dn + 1;
        if (!dayGroups[nextDn]) dayGroups[nextDn] = [];
        dayGroups[nextDn].unshift(overflow);

        // Re-optimize both affected days
        dayGroups[dn] = await optimizeDayGroup(dn, dayGroups[dn]);
        dayGroups[nextDn] = await optimizeDayGroup(nextDn, dayGroups[nextDn]);

        anyOverflow = true;
        onProgress?.(3, 5, `Day ${dn} overflow → moved ${overflow.company || 'job'} to Day ${nextDn}`);
      }
    }

    if (!anyOverflow) break;
  }

  // ── Step 5: Build final result ──
  onProgress?.(4, 5, 'Finalizing assignments...');

  sortedDays = Object.keys(dayGroups).map(Number).sort((a, b) => a - b);
  const result = [];

  // Pinned jobs stay as-is
  for (const p of pinned) {
    result.push({ id: p.id, day_number: p.day_number, sort_order: p.sort_order });
  }

  for (const dn of sortedDays) {
    const dayJobs = dayGroups[dn];
    let sortOrder = 1;
    const pinnedOnDay = pinned.filter(p => p.day_number === dn).sort((a, b) => a.sort_order - b.sort_order);
    const pinnedOrders = new Set(pinnedOnDay.map(p => p.sort_order));

    for (const job of dayJobs) {
      while (pinnedOrders.has(sortOrder)) sortOrder++;
      result.push({ id: job.id, day_number: dn, sort_order: sortOrder++ });
    }
  }

  return result;
};

// ─── Optimize single day (reorder within day only) ────────

/**
 * @param {Array} dayJobs - JOB items for one day (flattened)
 * @param {string} startPoint - origin address (home base or hotel area)
 * @param {string} endPoint - destination address (home base or hotel area)
 * @returns {Array} [{ id, sort_order }]
 */
export const optimizeDay = async (dayJobs, startPoint, endPoint) => {
  const unpinned = dayJobs.filter(j => !j.is_pinned);
  const pinnedJobs = dayJobs.filter(j => j.is_pinned);

  const withLoc = unpinned.filter(j => j.location);
  const noLoc = unpinned.filter(j => !j.location);

  if (withLoc.length < 2) {
    return dayJobs.map(j => ({ id: j.id, sort_order: j.sort_order }));
  }

  const waypoints = withLoc.map(j => j.location);
  const order = await getOptimizedOrder(startPoint, endPoint, waypoints);
  const reordered = [...order.map(i => withLoc[i]), ...noLoc];

  const pinnedOrders = new Set(pinnedJobs.map(p => p.sort_order));
  const result = pinnedJobs.map(p => ({ id: p.id, sort_order: p.sort_order }));

  let sortOrder = 1;
  for (const job of reordered) {
    while (pinnedOrders.has(sortOrder)) sortOrder++;
    result.push({ id: job.id, sort_order: sortOrder++ });
  }

  return result;
};
